import { FunctionComponent, useCallback, useEffect, useMemo } from "react";
import {
  Avatar,
  ButtonDS,
  CritAir,
  DownloadOutlined,
  EnergyClass,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  Tag,
  TextCapitalized,
  dateFormatterDayMonthYear,
  dateFormatterDayMonthYearLong2Digits,
  dateFormatterHourMinute,
  uppercaseFirstLetter,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  selectAdvancedAnalyticsVehiclesList,
  selectDriversHistoryList,
} from "../fleetSlice";
import { createFileXlsx } from "@qivia/ui/src/designSystem/components/materialUiTable/createFileXlsx";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import "../../../../i18n/i18n";
import { fakeAdvancedAnalyticsVehiclesForDemo } from "../lib";

export const AdvancedAnalyticsVehicleSidePanel: FunctionComponent = () => {
  const pageName = "fleet";
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const advancedAnalyticsVehicleListInit = useAppSelector(
    selectAdvancedAnalyticsVehiclesList,
  );
  const firstPath = location.pathname.split("/")[1]; // home or departments

  const isDemoEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] === "demo";
  }, []);

  const allDriversHistoryList = useAppSelector(selectDriversHistoryList);
  const vehicle = useMemo(
    () =>
      isDemoEnvironment()
        ? [
            ...advancedAnalyticsVehicleListInit,
            ...fakeAdvancedAnalyticsVehiclesForDemo,
          ].find((v) => v.uuid === params.id)
        : advancedAnalyticsVehicleListInit.find((v) => v.uuid === params.id),
    [advancedAnalyticsVehicleListInit, isDemoEnvironment, params.id],
  );

  const driversHistoryList = useMemo(
    () => allDriversHistoryList.filter((dh) => dh.vehicleUuid === params.id),
    [allDriversHistoryList, params.id],
  );

  const lastDriver = useMemo(() => driversHistoryList[0], [driversHistoryList]);

  const driversHistoryExcelColumns = useMemo(
    () => [
      "analyticCode1",
      "lastName",
      "firstName",
      "startOfUsage",
      "startOfUsageTime",
      "endOfUsage",
      "endOfUsageTime",
    ],
    [],
  );

  const handleClickDriver = useCallback(() => {
    navigate(`/${firstPath}/drivers/${lastDriver?.driverUuid}`);
  }, [lastDriver?.driverUuid, navigate, firstPath]);

  const handleDownloadDriverHistory = useCallback(() => {
    if (driversHistoryList.length <= 0) return;
    const headerTitles = driversHistoryExcelColumns.map((key) =>
      uppercaseFirstLetter(t(`driversHistory.column.${key}`)),
    );

    const bodyList = driversHistoryList.map((dh) => [
      dh.driverInfo.analyticCode1 ?? "",
      dh.driverInfo.lastName,
      dh.driverInfo.firstName,
      dateFormatterDayMonthYearLong2Digits(new Date(dh.startOfUsage)),
      dateFormatterHourMinute(new Date(dh.startOfUsage)),
      dh.endOfUsage
        ? dateFormatterDayMonthYearLong2Digits(new Date(dh.endOfUsage))
        : "",
      dh.endOfUsage ? dateFormatterHourMinute(new Date(dh.endOfUsage)) : "",
    ]);

    const file = createFileXlsx({
      tabTitle: t(`${pageName}.view.title`),
      headerTitles,
      body: bodyList,
    });

    if (!file) return;
    void dispatch(
      documentDownloadXlsxAsync({
        buffer: file,
        fileName: "QIVIA_" + t("vehicles.driversHistory.excel.title") + ".xlsx",
      }),
    );
  }, [dispatch, driversHistoryExcelColumns, driversHistoryList, t]);

  useEffect(() => {
    if (!vehicle) {
      navigate(`/${firstPath}/fleet/advanced_analytics`);
    }
  }, [navigate, vehicle, firstPath]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/fleet/advanced_analytics`);
  }, [navigate, firstPath]);

  if (!vehicle) {
    return;
  }

  return (
    <SidePanelDS.SidePanel visible={true} onClose={onSidePanelClosed}>
      <SidePanelDS.Section
        verticalSpacing={2}
        horizontalSpacing={1.5}
        background={colors["colors/button/secondary/hover"]}
      >
        <StyledPanelContainer>
          <StyledContent>{vehicle.registrationNumber}</StyledContent>
          {vehicle.reference && (
            <>
              <Spacer y={0.5} />
              <Tag
                text={vehicle.reference}
                borderColor={colors["colors/borders/cells/cells"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                textColor={colors["colors/button/primary/default"]}
              />
            </>
          )}
          <StyledImg src={Car} alt="Car" />
          <Spacer y={1} />
          <StyledContent>
            {`${vehicle.marque ? vehicle.marque : ""} ${vehicle.modele ? vehicle.modele : ""}`.trim()}
          </StyledContent>
          {vehicle.energie && (
            <>
              <Spacer y={0.25} />
              <StyledEnergie>
                <TextCapitalized>
                  {vehicle.energie && t(`vehicles.energie.${vehicle.energie}`)}
                </TextCapitalized>
              </StyledEnergie>
            </>
          )}
        </StyledPanelContainer>
        <Spacer y={1.5} />
      </SidePanelDS.Section>
      <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>{t("vehicles.sidepanel.details")}</TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.status")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <TextCapitalized>
              {t(`vehicles.status.${vehicle.status}`)}
            </TextCapitalized>
          }
        />
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>{t("vehicles.sidepanel.type")}</TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <Tag
              text={t(`vehicles.type.${vehicle.vehicleType}`)}
              borderColor={colors["colors/borders/cells/cells"]}
              backgroundColor={
                colors["colors/surfaces/background/background_level0"]
              }
              textColor={colors["colors/text/darkGrey"]}
            />
          }
        />
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>
            {t("vehicles.sidepanel.driversHistory")}
          </TextCapitalized>
        </SectionTitle>
        {lastDriver && (
          <>
            <Spacer y={2} />
            <SidePanelDS.ActionBlock>
              <SidePanelDS.SectionedActionStripe
                left={
                  <StyledRowCenter>
                    <TextCapitalized>
                      <Avatar
                        size={"S"}
                        color={"orange"}
                        lastName={lastDriver.driverInfo.lastName}
                        firstName={lastDriver.driverInfo.firstName}
                      />
                    </TextCapitalized>
                    <Spacer x={0.5} />
                    {lastDriver.driverInfo.firstName.concat(
                      " ",
                      lastDriver.driverInfo.lastName,
                    )}
                  </StyledRowCenter>
                }
                right={
                  <StyledLittleIcon>
                    <RemoveRedEyeOutlined
                      key="driver"
                      onClick={handleClickDriver}
                    />
                    <Spacer x={1} />
                  </StyledLittleIcon>
                }
              />
            </SidePanelDS.ActionBlock>
            <Spacer y={1} />
            <ButtonDS
              sizeButton="S"
              format="hug"
              buttonType="secondary"
              text={t("vehicles.sidepanel.downloadDriversHistoryList")}
              leftIcon={<DownloadOutlined />}
              onClick={handleDownloadDriverHistory}
            />
          </>
        )}
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>
            {t("vehicles.sidepanel.informations")}
          </TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.mileage")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {!vehicle.mileage ? "-" : vehicle.mileage.toFixed(2)}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.mileage.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <Spacer y={0.25} />
        {vehicle.lastMileageDate && (
          <StyledSubTitle>
            <TextCapitalized>
              {t("vehicles.sidepanel.mileageDate")}
            </TextCapitalized>
            <Spacer x={0.25} />:<Spacer x={0.25} />
            {dateFormatterDayMonthYear(new Date(vehicle.lastMileageDate))}
          </StyledSubTitle>
        )}
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.projectionEndOfContract.title")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {(vehicle.projectionEndOfContract
                ? vehicle.projectionEndOfContract
                : "-") +
                " / " +
                (vehicle.contractMileage ? vehicle.contractMileage : "-")}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.projectionEndOfContract.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.consumption.title")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {!vehicle.averageConsumption ? "-" : vehicle.averageConsumption}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.consumption.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <StyledSubTitle>
          <TextCapitalized>
            {t("vehicles.sidepanel.consumption.subTitle")}
          </TextCapitalized>
        </StyledSubTitle>
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>{t("vehicles.sidepanel.pollution")}</TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />

        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.emission")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {vehicle.averageEmission === null ? "-" : vehicle.averageEmission}
              <Spacer x={0.25} />
              <StyledSubTitle>
                {t("vehicles.sidepanel.emission.units")}
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <Spacer y={2} />

        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.energyClass.title")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {vehicle.energyClass === null ? (
                "-"
              ) : (
                <>
                  <EnergyClass
                    energyClass={vehicle.energyClass as EnergyClass}
                  />
                  <Spacer x={0.25} />
                </>
              )}
              <Spacer x={0.25} />
            </StyledRow>
          }
        />
        <Spacer y={0.25} />
        {vehicle.energyClass && (
          <StyledSubTitle>
            <TextCapitalized>
              {t(`vehicles.sidepanel.energyClass.${vehicle.energyClass}`)}
            </TextCapitalized>
            <Spacer x={0.25} />
          </StyledSubTitle>
        )}
        <Spacer y={2} />

        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.critAir")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {vehicle.critAir === null ? (
                <>
                  {"-"} <Spacer x={0.25} />
                </>
              ) : (
                <CritAir critAir={`${vehicle.critAir}`.toString() as CritAir} />
              )}
            </StyledRow>
          }
        />
      </SidePanelDS.Section>
    </SidePanelDS.SidePanel>
  );
};

const StyledSubTitle = styled.div`
  display: flex;
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledRow = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledContent = styled.div`
  ${typographies["Header/H4"]};
`;

const StyledImg = styled.img`
  filter: grayscale(100%);
  width: 15rem;
  height: 6.5rem;
  object-fit: contain;
`;

const StyledEnergie = styled.span`
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
  text-align: center;
`;
